import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchWeather, type WeatherSnapshot } from '@/lib/weather';

const STORAGE_KEY = 'nautmax-weather-city';

export interface SavedCity {
  name: string;
  admin1?: string;
  country?: string;
  latitude: number;
  longitude: number;
}

const DEFAULT_CITY: SavedCity = {
  name: 'São Paulo',
  admin1: 'São Paulo',
  country: 'Brasil',
  latitude: -23.5505,
  longitude: -46.6333,
};

function readCity(): SavedCity {
  if (typeof window === 'undefined') return DEFAULT_CITY;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CITY;
    const parsed = JSON.parse(raw) as SavedCity;
    if (
      typeof parsed?.latitude === 'number' &&
      typeof parsed?.longitude === 'number' &&
      typeof parsed?.name === 'string'
    ) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_CITY;
}

export function useWeather() {
  const [city, setCityState] = useState<SavedCity>(readCity);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(city));
    } catch {
      /* ignore */
    }
  }, [city]);

  const query = useQuery<WeatherSnapshot>({
    queryKey: ['weather', city.latitude, city.longitude],
    queryFn: ({ signal }) => fetchWeather(city.latitude, city.longitude, signal),
    staleTime: 10 * 60 * 1000,
    refetchInterval: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    city,
    setCity: setCityState,
    snapshot: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}
